import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SeoPages from "./SeoPages";
import Blogs from "./Blogs";

const API = process.env.REACT_APP_API_URL || "http://localhost:8080";

const SIDEBAR_W = 240;

const NAV_ITEMS = [
  { key: "overview", icon: "fa-chart-bar",   label: "Overview" },
  { key: "seo",      icon: "fa-search",       label: "SEO Pages" },
  { key: "blog",     icon: "fa-pencil-square-o", label: "Blog Posts" },
];

const StatCard = ({ label, value, icon, accent }) => (
  <div style={{ background: "#fff", borderRadius: 12, padding: "20px 24px", boxShadow: "0 1px 8px rgba(0,0,0,.07)", display: "flex", alignItems: "center", gap: 16 }}>
    <div style={{
      width: 50, height: 50, borderRadius: 12, background: accent + "18",
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
    }}>
      <i className={`fa ${icon}`} style={{ color: accent, fontSize: 20 }} />
    </div>
    <div>
      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#111", lineHeight: 1.2 }}>{value ?? "—"}</div>
    </div>
  </div>
);

const SectionCard = ({ title, children }) => (
  <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,.07)", overflow: "hidden" }}>
    <div style={{ padding: "16px 20px", borderBottom: "1px solid #f3f4f6" }}>
      <h6 style={{ margin: 0, fontWeight: 600, fontSize: 14, color: "#111" }}>{title}</h6>
    </div>
    <div style={{ padding: "4px 0" }}>{children}</div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats]   = useState(null);
  const [error, setError]   = useState("");
  const [tab, setTab]       = useState("overview");
  const [sideOpen, setSideOpen] = useState(false);
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const res = await fetch(`${API}/?url=admin/stats&_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/login");
        return;
      }
      setStats(await res.json());
    } catch {
      setError("Failed to load stats");
    }
  }, [navigate]);

  useEffect(() => {
    fetchStats();
    const id = setInterval(fetchStats, 30000);
    return () => clearInterval(id);
  }, [fetchStats]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    navigate("/login");
  };

  const PAGE_TITLES = { overview: "Overview", seo: "SEO Pages", blog: "Blog Posts" };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f5f6fa", fontFamily: "inherit" }}>

      {/* ── Sidebar ── */}
      <aside style={{
        width: SIDEBAR_W, background: "#1a1a1a", display: "flex", flexDirection: "column",
        position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 200,
        overflow: "hidden", boxSizing: "border-box",
      }}>
        {/* Logo */}
        <div style={{ padding: "20px 16px 18px", borderBottom: "1px solid #333", textAlign: "center", overflow: "hidden" }}>
          <div style={{ display: "inline-flex", background: "#fff", borderRadius: 8, padding: "6px 14px", marginBottom: 10, maxWidth: "100%" }}>
            <img
              src="/images/clean_logo.png"
              alt="Clear & Clean"
              style={{ height: 38, maxWidth: "100%", objectFit: "contain", display: "block" }}
              onError={(e) => { e.target.parentElement.style.display = "none"; }}
            />
          </div>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 13, letterSpacing: 0.3 }}>Clear &amp; Clean</div>
          <div style={{ color: "#888", fontSize: 11, marginTop: 2 }}>Admin Portal</div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: "16px 12px" }}>
          {NAV_ITEMS.map(({ key, icon, label }) => (
            <button
              key={key}
              onClick={() => { setTab(key); setSideOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 12, width: "100%",
                padding: "10px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                marginBottom: 4, fontWeight: 500, fontSize: 14, textAlign: "left",
                background: tab === key ? "#fff" : "transparent",
                color: tab === key ? "#1a1a1a" : "#aaa",
                transition: "all .15s"
              }}
            >
              <i className={`fa ${icon}`} style={{ width: 16, textAlign: "center", fontSize: 14 }} />
              {label}
            </button>
          ))}
        </nav>

        {/* Sidebar footer */}
        <div style={{ padding: "16px 12px", borderTop: "1px solid #333" }}>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            style={{
              display: "flex", alignItems: "center", gap: 10, padding: "9px 14px",
              borderRadius: 8, color: "#aaa", fontSize: 13, textDecoration: "none",
              marginBottom: 6
            }}
          >
            <i className="fa fa-external-link" style={{ fontSize: 13 }} />
            View Website
          </a>
          <button
            onClick={logout}
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%",
              padding: "9px 14px", borderRadius: 8, border: "none", cursor: "pointer",
              background: "transparent", color: "#ef4444", fontSize: 13, fontWeight: 500
            }}
          >
            <i className="fa fa-sign-out" style={{ fontSize: 13 }} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main area ── */}
      <div style={{ marginLeft: SIDEBAR_W, flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>

        {/* Topbar */}
        <header style={{
          background: "#fff", borderBottom: "1px solid #e5e7eb",
          padding: "0 28px", height: 60, display: "flex", alignItems: "center",
          justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h6 style={{ margin: 0, fontWeight: 600, fontSize: 16, color: "#111" }}>
              {PAGE_TITLES[tab]}
            </h6>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={fetchStats}
              style={{
                background: "#f3f4f6", border: "none", borderRadius: 8,
                padding: "6px 14px", fontSize: 13, cursor: "pointer", color: "#374151",
                display: "flex", alignItems: "center", gap: 6
              }}
            >
              <i className="fa fa-refresh" style={{ fontSize: 12 }} /> Refresh
            </button>
            <div style={{
              width: 34, height: 34, borderRadius: "50%", background: "#1a1a1a",
              display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14
            }}>
              <i className="fa fa-user" />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, padding: "28px 28px 40px" }}>
          {error && (
            <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", color: "#b91c1c", borderRadius: 8, padding: "10px 16px", marginBottom: 20, fontSize: 14 }}>
              <i className="fa fa-exclamation-circle me-2" />{error}
            </div>
          )}

          {/* ── Overview tab ── */}
          {tab === "overview" && (
            <>
              {/* Stat cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
                <StatCard label="Today"      value={stats?.today}  icon="fa-calendar-check-o" accent="#3b82f6" />
                <StatCard label="This Week"  value={stats?.week}   icon="fa-calendar"          accent="#10b981" />
                <StatCard label="This Month" value={stats?.month}  icon="fa-calendar-o"        accent="#f59e0b" />
                <StatCard label="All Time"   value={stats?.total}  icon="fa-globe"             accent="#8b5cf6" />
              </div>

              {/* Tables row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>

                {/* By page */}
                <SectionCard title="Top Pages">
                  {stats?.by_page?.length ? (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "#f9fafb" }}>
                          <th style={{ padding: "8px 20px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 12 }}>Page</th>
                          <th style={{ padding: "8px 20px", textAlign: "right", fontWeight: 600, color: "#6b7280", fontSize: 12 }}>Visits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.by_page.map((r) => (
                          <tr key={r.page} style={{ borderTop: "1px solid #f3f4f6" }}>
                            <td style={{ padding: "9px 20px", color: "#374151" }}>{r.page}</td>
                            <td style={{ padding: "9px 20px", textAlign: "right", fontWeight: 600, color: "#111" }}>{r.visits}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p style={{ padding: "16px 20px", color: "#9ca3af", fontSize: 13, margin: 0 }}>No data yet</p>}
                </SectionCard>

                {/* By day */}
                <SectionCard title="Daily Visits — Last 7 Days">
                  {stats?.by_day?.length ? (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "#f9fafb" }}>
                          <th style={{ padding: "8px 20px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 12 }}>Date</th>
                          <th style={{ padding: "8px 20px", textAlign: "right", fontWeight: 600, color: "#6b7280", fontSize: 12 }}>Visits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.by_day.map((r) => (
                          <tr key={r.day} style={{ borderTop: "1px solid #f3f4f6" }}>
                            <td style={{ padding: "9px 20px", color: "#374151" }}>{r.day}</td>
                            <td style={{ padding: "9px 20px", textAlign: "right", fontWeight: 600, color: "#111" }}>{r.visits}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p style={{ padding: "16px 20px", color: "#9ca3af", fontSize: 13, margin: 0 }}>No data yet</p>}
                </SectionCard>
              </div>

              {/* Recent visitors */}
              <SectionCard title="Recent Visitors">
                {stats?.recent?.length ? (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "#f9fafb" }}>
                          <th style={{ padding: "8px 20px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 12 }}>Page</th>
                          <th style={{ padding: "8px 20px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 12 }}>IP Address</th>
                          <th style={{ padding: "8px 20px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 12 }}>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recent.map((r, i) => (
                          <tr key={i} style={{ borderTop: "1px solid #f3f4f6" }}>
                            <td style={{ padding: "9px 20px", color: "#374151" }}>{r.page}</td>
                            <td style={{ padding: "9px 20px", color: "#6b7280", fontFamily: "monospace", fontSize: 12 }}>{r.ip}</td>
                            <td style={{ padding: "9px 20px", color: "#6b7280" }}>{new Date(r.visited_at).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p style={{ padding: "16px 20px", color: "#9ca3af", fontSize: 13, margin: 0 }}>No visitors yet</p>}
              </SectionCard>
            </>
          )}

          {tab === "seo"  && <SeoPages />}
          {tab === "blog" && <Blogs />}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
